import type { FC, ReactElement } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalSettings } from 'providers';
import type { ServiceNetwork } from 'providers';

import FacebookIcon from 'assets/icon-meta.svg?react';
import IconTikTok from 'assets/icon-tiktok.svg?react';
import IconX from 'assets/icon-x.svg?react';
import IconPinterest from 'assets/icon-pinterest.svg?react';

const NETWORK_OPTIONS: { code: ServiceNetwork; name: string }[] = [
  { code: 'facebook', name: 'Facebook Ads' },
  { code: 'tiktok', name: 'TikTok Ads' },
  { code: 'twitter', name: 'X Ads' },
  { code: 'pinterest', name: 'Pinterest Ads' },
];

const NETWORK_ICONS: Record<ServiceNetwork, ReactElement> = {
  facebook: <FacebookIcon style={{ width: 26, height: 26 }} />,
  tiktok: <IconTikTok width={22} height={26} />,
  twitter: <IconX width={22} height={22} />,
  pinterest: <IconPinterest width={24} height={24} />,
};

const getNetworkFromPath = (pathname: string): ServiceNetwork | undefined => {
  if (pathname.includes('/services/facebook')) return 'facebook';
  if (pathname.includes('/services/tiktok')) return 'tiktok';
  if (pathname.includes('/services/twitter')) return 'twitter';
  if (pathname.includes('/services/pinterest')) return 'pinterest';

  return undefined;
};

const NetworkButton: FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { selectedNetwork, setSelectedNetwork } = useGlobalSettings();

  const routeNetwork = useMemo(() => getNetworkFromPath(pathname), [pathname]);

  const currentValue = selectedNetwork ?? routeNetwork ?? '';

  useEffect(() => {
    if (!routeNetwork || routeNetwork === selectedNetwork) return;

    setSelectedNetwork(routeNetwork);
  }, [routeNetwork, selectedNetwork, setSelectedNetwork]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<ServiceNetwork | ''>) => {
      const value = event.target.value as ServiceNetwork;
      if (!value) return;

      setSelectedNetwork(value);
      navigate(`/services/${value}`);
    },
    [navigate, setSelectedNetwork],
  );

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        '&:hover .network-caret': { opacity: 1 },
      }}
    >
      <FormControl>
        <MuiSelect<ServiceNetwork | ''>
          inputProps={{ IconComponent: () => null, 'aria-label': 'Ad network' }}
          variant="outlined"
          id="global-network-select"
          value={currentValue}
          onChange={handleChange}
          displayEmpty
          sx={{
            width: 42,
            maxWidth: 42,
            minWidth: 42,
            height: 42,
            borderRadius: 1.5,
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s ease',
            '& [role="combobox"].MuiSelect-select.MuiSelect-outlined': {
              p: 0,
              width: 44,
              maxWidth: 44,
              minWidth: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .MuiOutlinedInput-notchedOutline': { border: 'none', boxShadow: 'none' },
            '& svg': { transition: 'transform 0.2s ease' },
            '&:hover': { backgroundColor: 'rgba(41, 121, 255, 0.08)' },
            '&:hover svg': { transform: 'scale(0.80)' },
          }}
          renderValue={(code) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {code ? NETWORK_ICONS[code] : NETWORK_ICONS.facebook}
            </Box>
          )}
        >
          {NETWORK_OPTIONS.map(({ name, code }) => (
            <MenuItem key={`networkButton.${code}`} value={code} sx={{ minWidth: 200, gap: 1.25 }}>
              <ListItemIcon sx={{ minWidth: 'auto' }}>{NETWORK_ICONS[code]}</ListItemIcon>
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </MuiSelect>
      </FormControl>
      <KeyboardArrowDownIcon
        className="network-caret"
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: -2,
          transform: 'translateX(-50%)',
          fontSize: 14,
          color: 'text.secondary',
          pointerEvents: 'none',
          opacity: 0,
          transition: 'opacity 0.2s ease',
        }}
      />
    </Box>
  );
};

export default NetworkButton;
